import Events from '@utilities/events';
import setTabIndexOfChildren from '@utilities/set-tabindex-of-children';
import ScreenDimensions from '@utilities/screen-dimensions';
import keyboardEventCode from '@utilities/keyboard-event-code';
import { body, html } from '@utilities/dom-elements';
import { addBodyLock, removeBodyLock } from '@utilities/lock-body';

const MODAL_HOOK = '[js-hook-modal]';
const MODAL_CLOSE_HOOK = '[js-hook-button-modal-close]';
const MODAL_VISIBLE_CLASS = 'modal--is-showing';
const MODAL_HTML_CLASS = 'is--modal-open';
const MODAL_ANIMATING_CLASS = 'modal--is-animating';
const CUSTOM_COUNTRY_MODAL_OPEN = 'is--country-modal-open';

class Modal {
  constructor() {
    this.registeredModals = {};
    this.scrollElement = document.scrollingElement || html;
    this.tabIndexExceptionIds = [
      'modal-mega-menu',
      'modal-main-menu',
      'modal-mobile-menu',
      'refinement-bar',
      'share-cart-modal',
      'coupon-modal',
    ];
    this.scrollTop = 0;

    const modals = [...document.querySelectorAll(MODAL_HOOK)];

    modals.forEach((modal) => this.setupModalRegistry(modal));

    this.bindEvents();
  }

  /**
   * Bind all button specific events
   * @param {string} id Modal id
   * @param {HTMLElement} triggerBtn Button to open modal
   */
  static bindTriggerButton({ id, triggerBtn }) {
    triggerBtn.forEach((triggerEl) =>
      triggerEl.addEventListener('click', () => {
        Events.$trigger(`modal::open`, { data: { id } });
        Events.$trigger(`modal[${id}]::open`, { data: { id } });
      }),
    );
  }

  /**
   * Bind all close button specific events
   * @param {string} id Modal id
   * @param {HTMLElement} triggerBtn Button to open modal
   */
  static bindCloseButton({ id, closeBtn }) {
    closeBtn.forEach((btn) =>
      btn.addEventListener('click', () => {
        Events.$trigger('modal::close', { data: { id } });
        Events.$trigger(`modal[${id}]::close`, { data: { id } });
      }),
    );
  }

  static clearCurrentFocus() {
    if (document.activeElement !== document.body) document.activeElement.blur();
  }

  static hideModal(modal) {
    if (!modal.modalIsOpen) {
      modal.classList.remove(MODAL_VISIBLE_CLASS);
      modal.classList.remove(MODAL_ANIMATING_CLASS);
    }
  }

  /**
   * Bind event based on custom hook
   * @param {string} id
   */
  customBind(id) {
    const modal = document.getElementById(id);

    this.setupModalRegistry(modal);
  }

  /**
   * Setup an object per found modal
   * @param {HTMLElement} el Single modalbox
   */
  setupModalRegistry(el) {
    if (el._modalIsInitialised) return;

    const id = el.getAttribute('id');

    const triggerBtn = [...document.querySelectorAll(`[aria-controls=${id}]`)];

    const closeBtn = [...el.querySelectorAll(MODAL_CLOSE_HOOK)];
    const mobileOnly = el.dataset.modalMobileOnly === 'true';

    const modal = {
      el,
      id,
      triggerBtn,
      closeBtn,
    };

    if (!mobileOnly || !ScreenDimensions.isTabletLandscapeAndBigger) {
      if (!this.tabIndexExceptionIds.includes(id)) {
        setTabIndexOfChildren(modal.el, -1);
      }
      this.registeredModals[`modal-${id}`] = modal;
    }

    this.bindModalEvents(modal);

    const openOnLoad = el.classList.contains(MODAL_VISIBLE_CLASS);
    if (openOnLoad) {
      Events.$trigger(`modal[${id}]::open`);
    }

    el._modalIsInitialised = true;
  }

  /**
   * Bind all general events
   */
  bindEvents() {
    Events.$on('modal::close', (event, data) => this.closeModal(data));
    Events.$on('modal::open', (event, data) => this.openModal(data));

    Events.$on('modal::bind', (event, data) => this.customBind(data));
    Events.$on('modal::bind-buttons', (event, data) => this.rebindTriggerButtons(data));
  }

  /**
   * Bind all modal specific events
   * @param {string} id Modal id
   * @param {HTMLElement} triggerBtn Button to open modal
   * @param {HTMLElement} closeBtn Button to close modal
   */
  bindModalEvents({ el, id, triggerBtn, closeBtn }) {
    Modal.bindTriggerButton({ el, id, triggerBtn });

    Events.$on(`modal[${id}]::close`, () => this.closeModal({ id }));
    Events.$on(`modal[${id}]::open`, () => this.openModal({ id }));

    Modal.bindCloseButton({ el, id, closeBtn });

    // Close on ESCAPE_KEY
    document.addEventListener('keyup', (event) => {
      if (keyboardEventCode.escape.indexOf(event.keyCode || event.key) !== -1 && body.classList.contains(MODAL_HTML_CLASS)) {
        Events.$trigger('modal::close');
        Events.$trigger(`modal[${id}]::close`, { data: { id } });
      }
    });
  }

  /**
   * Rebind all buttons of existing modals
   * @param {string} id Modal id
   */
  rebindTriggerButtons({id}) {
    const modals = [...document.querySelectorAll(`#${id}`)];

    // Loop trough all found modals based on hook
    modals.forEach((modal) => {
      const registeredModal = this.registeredModals[`modal-${modal.id}`] || false;

      if (registeredModal) {
        const triggerBtn = [...document.querySelectorAll(`[aria-controls=${modal.id}]`)];
        const closeBtn = [...registeredModal.el.querySelectorAll(MODAL_CLOSE_HOOK)];
        registeredModal.triggerBtn = triggerBtn;
        registeredModal.closeBtn = closeBtn;
        Modal.bindTriggerButton(registeredModal);
        Modal.bindCloseButton(registeredModal);
      }
    });
  }

  /**
   * Open modal by given id
   * @param {Object[]} data
   * @param {string} data[].id
   */
  openModal(data) {
    const modal = this.registeredModals[`modal-${data.id}`];

    if (!modal || modal.el.modalIsOpen) return;

    const autoFocus = modal.el.dataset.modalAutoFocus === 'true';
    const noBodyClass = modal.el.dataset.modalNoBodyClass === 'true';
    const closeAllOthers = modal.el.dataset.modalCloseAllOthers === 'true';
    const keepScrollPosition = modal.el.dataset.modalKeepScrollPosition === 'true';
    const hasTransform = modal.el.dataset.modalHasTransform === 'true';

    // Set scroll position for fixed body on mobile
    if (keepScrollPosition && !ScreenDimensions.isTabletPortraitAndBigger) this.setScrollPosition();

    if (closeAllOthers) {
      Object.keys(this.registeredModals)
        .filter((key) => this.registeredModals[key].id !== data.id)
        .forEach((id) => {
          const _modal = this.registeredModals[id];
          if (_modal.el.modalIsOpen) {
            Events.$trigger(`modal[${_modal.id}]::close`, { data: { id: _modal.id } });
          }
        });
    }

    // Add modal open class to body element if noBodyClass is false
    if (!noBodyClass) body.classList.add(MODAL_HTML_CLASS);

    // Add tabindex and add visible class
    if (!this.tabIndexExceptionIds.includes(data.id)) {
      modal.el.tabIndex = 0;
      setTabIndexOfChildren(modal.el, 0);
    }

    setTimeout(() => {
      modal.el.classList.add(MODAL_VISIBLE_CLASS);
    }, 0);

    if (hasTransform) {
      setTimeout(() => {
        modal.el.classList.add(MODAL_ANIMATING_CLASS);
      }, 10);
    }

    modal.el.modalIsOpen = true;
    addBodyLock();

    Events.$trigger(`modal::opened[${data.id}]`);

    Events.$trigger('focustrap::activate', {
      data: {
        element: modal.el,
        autoFocus,
      },
    });
  }

  /**
   * Close modal by id, if none gives it will close all
   * @param {Object[]} data
   * @param {string} data[].id
   */
  closeModal(data) {
    const { id } = data || {};

    // If no ID is given we will close all modals
    if (!data || !id) {
      // eslint-disable-next-line
      for (const modalIndex of Object.keys(this.registeredModals)) {
        const modal = this.registeredModals[modalIndex] || false;
        if (modal && modal.id) {
          this.closeModal({ id: modal.id });
          Events.$trigger('focustrap::deactivate');
        }
      }

      return;
    }

    // Get current modal from all known modals
    const modal = this.registeredModals[`modal-${id}`];

    // If there is no modal do nothing
    if (!modal || !modal.el.modalIsOpen) return;

    const noBodyClass = modal.el.dataset.modalNoBodyClass === 'true';

    // Remove modal open class off body element if noBodyClass is false
    if (!noBodyClass) body.classList.remove(MODAL_HTML_CLASS);

    const keepScrollPosition = modal.el.dataset.modalKeepScrollPosition === 'true';

    // Scroll to original position
    if (keepScrollPosition && !ScreenDimensions.isTabletPortraitAndBigger)
      this.removeScrollPosition();

    // Remove tabindex and remove visible class
    if (!this.tabIndexExceptionIds.includes(id)) {
      modal.el.tabIndex = -1;
      setTabIndexOfChildren(modal.el, -1);
    }

    const hasTransform = modal.el.dataset.modalHasTransform === 'true';

    if (hasTransform) modal.el.classList.remove(MODAL_ANIMATING_CLASS);

    modal.el.classList.remove(MODAL_VISIBLE_CLASS);
    body.classList.remove(CUSTOM_COUNTRY_MODAL_OPEN);

    modal.el.modalIsOpen = false;
    removeBodyLock();

    Events.$trigger('focustrap::deactivate');

    Modal.clearCurrentFocus();
  }

  /**
   * Sets scrollposition to prevent body scrolling to top when position is fixed
   */
  setScrollPosition() {
    this.scrollTop = this.scrollElement.scrollTop;
    body.style.top = `-${this.scrollTop}px`;
  }

  /**
   * Removes scroll position from body and scrolls to original position
   */
  removeScrollPosition() {
    this.scrollElement.scrollTop = this.scrollTop;
    body.style.removeProperty('top');
  }
}

export default Modal;
